import { useEffect, useRef, useState } from 'react'
import { fetchCached, getCached, setCached, useCached } from '../lib/cache'
import { nyToday } from '../state/day'
import { useNav } from '../lib/nav'
import { Page, TopBar } from '../components/Shell'
import { SignSheet } from '../components/SignSheet'
import { Skeleton, Empty } from '../components/Skeleton'
import { useToast } from '../components/Toast'
import { I } from '../components/Icons'
import { cancelSale, cancelTrade, loadLast7, loadSales, loadToday, voidSale, voidTrade } from '../lib/api'
import { useLiveRows } from '../lib/live'
import { reduced } from '../lib/motion'
import { fmt2, fmtC, TENDER_LABEL, type SaleRow, type Employee } from '../lib/types'

const METHOD = TENDER_LABEL
const tenders = (s: SaleRow) => (s.payments ?? []).length > 1 ? 'Split: ' + s.payments!.map((p) => `${TENDER_LABEL[p.method]} ${fmt2(p.amount_cents)}${p.status === 'received' ? '' : ' (open)'}`).join(' + ') : METHOD[s.payment_method]

export function Sales() {
  const nav = useNav()
  const toast = useToast()
  // D1: the last lists shown come up at once from the read cache and are re-read from the server every time the screen opens
  type Tot = { payment_method: string; source: string; sales: number; total_cents: number }
  const todayKey = `today:${nyToday()}`
  const S = useCached<SaleRow[]>('sales', () => loadSales(), { staleMs: 0 })
  const T = useCached<Tot[]>(todayKey, loadToday, { staleMs: 0 })
  const W = useCached<Tot[]>('last7', loadLast7, { staleMs: 0 })
  // a sale recorded or settled on another iPad (or by the webhook) shows here without a tap (L2); a short wait folds a burst into one read
  const bump = useRef(0)
  useLiveRows('sales', '', () => { window.clearTimeout(bump.current); bump.current = window.setTimeout(() => { void S.refresh(); void T.refresh(); void W.refresh() }, 600) })
  useEffect(() => () => window.clearTimeout(bump.current), [])
  const rows = S.data ?? null
  const today = T.data ?? []
  const week = W.data ?? []
  const [voiding, setVoiding] = useState<SaleRow | null>(null)
  const [cancelling, setCancelling] = useState<SaleRow | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const load = () => Promise.all([fetchCached('sales', () => loadSales()), fetchCached(todayKey, loadToday), fetchCached('last7', loadLast7)]).then(() => {}).catch((e) => setErr((e as Error).message))
  const loadErr = S.error || T.error || W.error
  const wk = (src: string) => week.filter((t) => t.source === src).reduce((a, t) => ({ n: a.n + t.sales, c: a.c + Number(t.total_cents) }), { n: 0, c: 0 })
  const tot = (m?: string, src?: string) => today.filter((t) => (!m || t.payment_method === m) && (!src || t.source === src)).reduce((a, t) => a + Number(t.total_cents), 0)
  const doVoid = async (owner: Employee) => {
    if (!voiding) return
    const row = voiding, before = getCached<SaleRow[]>('sales')
    setBusy(true); setErr(''); setVoiding(null)
    setCached<SaleRow[]>('sales', (prev) => (prev ?? []).map((r) => (r.id === row.id ? { ...r, voided: true, voided_by: owner.employee_id } : r)))   // D4: at once
    try { if (row.trade_id) await voidTrade(row.trade_id, owner.employee_id, 'Voided from the Sales screen'); else await voidSale(row.id, owner.employee_id, 'Voided from the Sales screen'); toast(`${row.trade_id && row.trades ? `Trade #${row.trades.trade_no}` : `Sale #${row.sale_no}`} voided. The pieces are back in stock.`); await load() }
    catch (e) { if (before) setCached('sales', before); setErr((e as Error).message) } finally { setBusy(false) }
  }
  const doCancel = async (e: Employee) => {
    if (!cancelling) return
    const row = cancelling, before = getCached<SaleRow[]>('sales')
    setBusy(true); setErr(''); setCancelling(null)
    setCached<SaleRow[]>('sales', (prev) => (prev ?? []).map((r) => (r.id === row.id ? { ...r, status: 'cancelled' } : r)))   // D4: at once
    try { if (row.trade_id) await cancelTrade(row.trade_id, e.employee_id); else await cancelSale(row.id, e.employee_id); toast(`${row.trade_id && row.trades ? `Trade #${row.trades.trade_no}` : `Sale #${row.sale_no}`} cancelled. Nothing changed in Shopify.`); await load() } catch (ex) { if (before) setCached('sales', before); setErr((ex as Error).message) } finally { setBusy(false) }
  }
  // Simple Sales (29 Sep, Tim: "it should be simple"): today's number, what is still waiting on payment, and the list.
  const isToday = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) === nyToday()
  const paidToday = rows ? rows.filter((s) => s.status === 'paid' && !s.voided && isToday(s.created_at)).length : 0
  const waiting = rows ? rows.filter((s) => (s.status === 'awaiting_payment' || s.status === 'awaiting_card') && !s.voided) : []
  const done = rows ? rows.filter((s) => !waiting.includes(s)) : []
  const inStore = tot(undefined, 'counter') + tot(undefined, 'pos'), web = tot(undefined, 'web')
  const weekTotal = wk("counter").c + wk("pos").c + wk("web").c
  const tenderLine = (['cash', 'zelle', 'crypto', 'paypal', 'card'] as const).map((m) => [m, tot(m)] as const).filter(([, v]) => v > 0)
  // motion: rows present at the first load fade up in order; a row that arrives later (another iPad, the website) drops in and flashes
  const seen = useRef<Set<string> | null>(null)
  if (seen.current === null && rows !== null) seen.current = new Set(rows.map((r) => r.id))
  const firstIds = useRef<Set<string> | null>(null)
  if (firstIds.current === null && rows !== null) firstIds.current = new Set(rows.map((r) => r.id))
  useEffect(() => { if (rows) rows.forEach((r) => seen.current?.add(r.id)) })
  const rowClass = (s: SaleRow) => (seen.current && !seen.current.has(s.id) ? ' sl-new' : firstIds.current?.has(s.id) ? ' sl-in' : '')
  const chip = (s: SaleRow) => s.voided ? ['Voided', 'off'] : s.status === 'cancelled' ? ['Cancelled', 'off'] : s.status === 'awaiting_card' ? ['Waiting for card', 'wait'] : s.status === 'awaiting_payment' ? ['Awaiting payment', 'wait'] : s.source === 'web' ? ['Website', 'web'] : s.source === 'pos' ? ['POS', 'web'] : ['Paid', 'ok']
  const Row = (s: SaleRow, i: number) => {
    const [label, tone] = chip(s)
    const off = s.voided || s.status === 'cancelled'
    return (
      <div key={s.id} className={'sl-row' + (off ? ' sl-row--off' : '') + rowClass(s)} style={{ ['--i' as string]: Math.min(i, 12) }}>
        <span className="sl-no">{s.trade_id && s.trades ? 'T' + s.trades.trade_no : s.sale_no}</span>
        <span className="sl-t"><b>{s.lines.map((l) => l.title).join(' + ') || (s.source === 'web' ? 'Website order' : 'Sale')}</b>
          <small>{isToday(s.created_at) ? '' : new Date(s.created_at).toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric' }) + ' · '}{new Date(s.created_at).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} · {tenders(s)}{s.shopify_order_name ? ` · ${s.shopify_order_name}` : ''}{s.customer_name ? ` · ${s.customer_name}` : ''}</small></span>
        <span className={'sl-chip sl-chip--' + tone}>{label}</span>
        <span className="sl-amt">{fmtC(s.total_cents)}</span>
        <span className="sl-act">
          {s.status === 'awaiting_payment' && !s.voided && <button type="button" className="seg__btn sl-primary" onClick={() => nav(s.trade_id ? `/trade/collect/${s.trade_id}` : `/sell/collect/${s.id}`)}>Collect</button>}
          {(s.status === 'awaiting_card' || s.status === 'awaiting_payment') && !s.voided && <button type="button" className="seg__btn" onClick={() => setCancelling(s)}>Cancel</button>}
          {!s.voided && s.status === 'paid' && s.source === 'counter' && <button type="button" className="seg__btn" onClick={() => setVoiding(s)}>Void</button>}
        </span>
      </div>
    )
  }
  return (
    <Page bar={<TopBar back="Home" backTo="/" title="Today's sales" caption={rows === null ? '' : `${paidToday} paid today`} />}>
      <div className="sl-hero">
        <div>
          <div className="sl-label">Today, all channels</div>
          <div className="sl-big">{T.data ? <Roll value={tot()} /> : '…'}</div>
          <div className="sl-sub">{T.data ? <>In store <b>{fmtC(inStore)}</b> · Website <b>{fmtC(web)}</b></> : '\u00a0'}</div>
        </div>
        <div className="sl-side"><b>{W.data ? fmtC(weekTotal) : '…'}</b><span>last 7 days</span></div>
        {tenderLine.length > 0 && <div className="sl-tenders">{tenderLine.map(([m, v]) => <span key={m}>{METHOD[m]} <b>{fmtC(v)}</b></span>)}</div>}
      </div>
      {(err || loadErr) && <div className="banner" style={{ background: '#d70015' }}>{err || (rows ? `Couldn't refresh (${loadErr}). Showing the last list.` : loadErr)}</div>}
      {waiting.length > 0 && <>
        <div className="section-title">Waiting on payment</div>
        <div className="card sl-list">{waiting.map(Row)}</div>
      </>}
      <div className="section-title">Sales</div>
      {rows === null && !err && !loadErr && <Skeleton rows={6} h={64} />}
      {rows !== null && <div className="card sl-list">
        {done.map((s, i) => Row(s, i + waiting.length))}
        {done.length === 0 && <Empty icon={I.sales(24)} line="No sales recorded yet today." action="New sale" onAction={() => nav('/sell')} />}
      </div>}
      <div className="hint">Pieces leave stock only when a payment is confirmed. Cancel an unpaid sale; Void a paid one (owner ID, puts the pieces back). Both stay on the record. Trades show as the sale of what the customer took.</div>
      {cancelling && <SignSheet title={`Cancel sale #${cancelling.sale_no}`} onConfirm={doCancel} onClose={() => !busy && setCancelling(null)} busy={busy} />}
      {voiding && <SignSheet title={`Void sale #${voiding.sale_no}`} ownerOnly onConfirm={doVoid} onClose={() => !busy && setVoiding(null)} busy={busy} />}
    </Page>
  )
}

/** A number that counts from 0 when the screen opens, then rolls to each new value (a sale landing). */
function Roll({ value }: { value: number }) {
  const [shown, setShown] = useState(0)
  const from = useRef(0)
  useEffect(() => {
    if (reduced()) { setShown(value); from.current = value; return }
    const start = from.current, t0 = performance.now()
    let raf = 0
    const tick = (now: number) => { const k = Math.min(1, (now - t0) / 700); setShown(start + (value - start) * (1 - Math.pow(1 - k, 3))); if (k < 1) raf = requestAnimationFrame(tick); else from.current = value }
    raf = requestAnimationFrame(tick)
    return () => { cancelAnimationFrame(raf); from.current = value }
  }, [value])
  return <>{fmtC(Math.round(shown))}</>
}
